import { Clock, Mail, MapPin, Menu, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router';
import { ArrowIcon, ButtonLink } from '@/components/ui/Button';
import { useSite } from '@/features/site/SiteProvider';
import { cn } from '@/lib/utils';

export const NAV_ITEMS = [
  { label: 'Home', to: '/' },
  { label: 'About', to: '/about' },
  { label: 'Apparel', to: '/apparel' },
  { label: 'Sports Goods', to: '/sports-goods' },
  { label: 'Private Label', to: '/private-label' },
  { label: 'Contact', to: '/contact' },
] as const;

export function Wordmark({ inverted = false, compact = false }: { inverted?: boolean; compact?: boolean }) {
  const { company } = useSite().settings;
  return (
    <Link to="/" className="group flex min-w-0 items-center gap-3" aria-label={`${company.legalName} — home`}>
      <span
        aria-hidden="true"
        className={cn(
          'grid h-9 w-9 shrink-0 place-items-center rounded-[6px] font-display text-[17px] font-extrabold',
          inverted ? 'bg-white text-primary' : 'bg-primary text-white',
        )}
      >
        {company.logoLetter || 'S'}
      </span>
      <span className="min-w-0 leading-none">
        <span className={cn('block truncate font-display text-[14.5px] font-extrabold tracking-[0.04em] uppercase sm:text-[15.5px]', inverted ? 'text-white' : 'text-primary')}>
          {company.shortName}
        </span>
        {!compact && (
          <span className={cn('mt-1 block truncate text-[10.5px] font-medium tracking-[0.14em] uppercase', inverted ? 'text-steel' : 'text-muted')}>
            {company.tagline}
          </span>
        )}
      </span>
    </Link>
  );
}

function TopBar() {
  const { settings, content } = useSite();
  const email = settings.contact.emails[0]?.email;
  return (
    <div className="bg-primary-dark text-[12px] text-steel">
      <div className="container-page flex h-10 items-center justify-between gap-6">
        <ul className="flex min-w-0 items-center gap-6">
          <li className="flex items-center gap-2 whitespace-nowrap">
            <MapPin className="h-3.5 w-3.5 shrink-0 text-white/70" aria-hidden />
            <span>
              Faisalabad <span className="text-white/40">&amp;</span> Sialkot, Pakistan
            </span>
          </li>
          {settings.contact.workingHours && (
            <li className="hidden items-center gap-2 whitespace-nowrap lg:flex">
              <Clock className="h-3.5 w-3.5 text-white/70" aria-hidden />
              {settings.contact.workingHours}
            </li>
          )}
        </ul>
        <ul className="flex items-center gap-6">
          {content.announcement.enabled && content.announcement.text && (
            <li className="hidden items-center gap-2 xl:flex">
              <span className="h-1.5 w-1.5 rounded-full bg-accent" aria-hidden />
              <Link to="/private-label" className="text-white/85 transition-colors hover:text-white">
                {content.announcement.text}
              </Link>
            </li>
          )}
          {email && (
            <li className="hidden sm:block">
              <a href={`mailto:${email}`} className="flex items-center gap-2 transition-colors hover:text-white">
                <Mail className="h-3.5 w-3.5 text-white/70" aria-hidden />
                {email}
              </a>
            </li>
          )}
          <li className="sm:hidden">
            <Link to="/contact#inquiry" className="font-medium text-white/85 hover:text-white">
              Private-label inquiries
            </Link>
          </li>
        </ul>
      </div>
    </div>
  );
}

function DesktopNav() {
  return (
    <nav aria-label="Main" className="hidden lg:block">
      <ul className="flex items-center gap-1">
        {NAV_ITEMS.map((item) => (
          <li key={item.to}>
            <NavLink
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                cn(
                  'relative flex h-10 items-center px-3 text-[14px] font-medium transition-colors',
                  'after:absolute after:inset-x-3 after:bottom-1 after:h-[2px] after:origin-left after:bg-accent after:transition-transform after:duration-300',
                  isActive ? 'text-primary after:scale-x-100' : 'text-ink-soft after:scale-x-0 hover:text-primary hover:after:scale-x-100',
                )
              }
            >
              {item.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function MobileMenu({ open, onClose }: { open: boolean; onClose: () => void }) {
  const panelRef = useRef<HTMLDivElement>(null);
  const { contact } = useSite().settings;

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panelRef.current?.querySelector<HTMLElement>('a')?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  return (
    <div
      id="mobile-nav"
      ref={panelRef}
      hidden={!open}
      className="fixed inset-x-0 top-[var(--header-h,72px)] bottom-0 z-40 overflow-y-auto bg-white lg:hidden"
    >
      <nav aria-label="Mobile" className="container-page flex min-h-full flex-col py-6">
        <ul className="divide-y divide-line border-y border-line">
          {NAV_ITEMS.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.to === '/'}
                onClick={onClose}
                className={({ isActive }) =>
                  cn('flex items-center justify-between py-4 font-display text-[20px] font-bold tracking-tight', isActive ? 'text-accent-dark' : 'text-primary')
                }
              >
                {item.label}
                <ArrowIcon className="text-muted" />
              </NavLink>
            </li>
          ))}
        </ul>
        <div className="mt-8 space-y-3">
          <ButtonLink to="/contact#inquiry" variant="accent" size="lg" className="w-full" onClick={onClose}>
            Request a Quote
          </ButtonLink>
          {contact.emails[0] && (
            <a href={`mailto:${contact.emails[0].email}`} className="block text-center text-[14px] text-muted underline-offset-4 hover:underline">
              {contact.emails[0].email}
            </a>
          )}
        </div>
        <p className="mt-auto pt-10 text-[13px] text-muted">{contact.workingHours}</p>
      </nav>
    </div>
  );
}

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const barRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => setOpen(false), [location.pathname, location.hash]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 48);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Expose the sticky bar height so the mobile panel sits exactly below it.
  useEffect(() => {
    const el = barRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => document.documentElement.style.setProperty('--header-h', `${el.getBoundingClientRect().bottom}px`));
    ro.observe(el);
    return () => ro.disconnect();
  }, [open]);

  return (
    <>
      <TopBar />
      <header
        ref={barRef}
        className={cn(
          'sticky top-0 z-50 border-b bg-white/95 backdrop-blur-md transition-[border-color,box-shadow] duration-300',
          scrolled || open ? 'border-line shadow-[0_8px_24px_-20px_rgba(11,23,38,0.5)]' : 'border-transparent',
        )}
      >
        <div className={cn('container-page flex items-center justify-between gap-6 transition-[height] duration-300', scrolled ? 'h-16' : 'h-[76px]')}>
          <Wordmark />
          <DesktopNav />
          <div className="flex items-center gap-2">
            {/* Visibility lives on a wrapper: a `hidden` class on the button would conflict with its own `inline-flex`. */}
            <span className="hidden md:block">
              <ButtonLink to="/contact#inquiry" variant="accent" icon={<ArrowIcon />}>
                Request a Quote
              </ButtonLink>
            </span>
            <button
              type="button"
              className="grid h-11 w-11 place-items-center rounded-[6px] border border-line text-primary transition-colors hover:border-primary lg:hidden"
              aria-expanded={open}
              aria-controls="mobile-nav"
              aria-label={open ? 'Close menu' : 'Open menu'}
              onClick={() => setOpen((o) => !o)}
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>
      <MobileMenu open={open} onClose={close} />
    </>
  );
}
